import { atom, ExtractAtomValue } from 'jotai';
import { atomWithReset, RESET } from 'jotai/utils';
import {
  parseAsBoolean,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs';

import type {
  ContentReference,
  SearchDocument as Document,
} from '@cfa/pathway-fe-shared/xp';

import { sanitizeInternalPath } from '@/utils/url';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SetDocumentActionPayload {
  document: Document | null;
  icon?: string;
  country?: ContentReference['country'];
}

// ---------------------------------------------------------------------------
// Base atom
// ---------------------------------------------------------------------------

export const documentAtom = atomWithReset({
  document: null as Document | null,
  icon: 'placeholder',
  hasTranslatedValue: false,
  isTranslating: false,
  chosenDocumentCountry: null as ContentReference['country'] | null,
});

// ---------------------------------------------------------------------------
// URL-encoded navigation state
// ---------------------------------------------------------------------------

const routedFromValues = ['search', 'browse', 'link', 'direct-link'] as const;

export type RoutedFrom = (typeof routedFromValues)[number];

/** Maps URL `from` param to Amplitude ResourceView `source`. */
export const resourceSourceFromRoutedFrom = (
  routedFrom: RoutedFrom,
): string => {
  if (routedFrom === 'link' || routedFrom === 'direct-link') {
    return 'direct link';
  }
  return routedFrom;
};

export const isDirectLinkRoutedFrom = (routedFrom: RoutedFrom): boolean =>
  routedFrom === 'link' || routedFrom === 'direct-link';

export const useRoutedFrom = () =>
  useQueryState(
    'from',
    parseAsStringLiteral(routedFromValues).withDefault('direct-link'),
  );

export const useBackUrl = () => {
  const [backUrl, setBackUrl] = useQueryState(
    'backUrl',
    parseAsString.withDefault(''),
  );
  return [sanitizeInternalPath(backUrl) ?? '', setBackUrl] as const;
};

export const useCategoryName = () =>
  useQueryState('cat', parseAsString.withDefault(''));

export const useSubcategoryName = () =>
  useQueryState('sub', parseAsString.withDefault(''));

// ---------------------------------------------------------------------------
// Action atoms
// ---------------------------------------------------------------------------

export const setDocumentAtom = atom(
  null,
  (get, set, payload: SetDocumentActionPayload) => {
    const prev = get(documentAtom);
    const { icon, document, country } = payload;

    set(documentAtom, {
      ...prev,
      icon: icon ?? 'placeholder',
      hasTranslatedValue:
        !!document?.otherContentUrl && document.sourceSystem === 'TRIDION',
      document,
      chosenDocumentCountry: country ?? null,
    });
  },
);

export const clearDocumentAtom = atom(null, (_, set) => {
  set(documentAtom, RESET);
});

export const updateDocumentAtom = atom(
  null,
  (get, set, partial: Partial<ExtractAtomValue<typeof documentAtom>>) => {
    const prev = get(documentAtom);
    set(documentAtom, {
      ...prev,
      ...partial,
      icon: 'icon' in partial ? (partial.icon ?? 'placeholder') : prev.icon,
    });
  },
);

// ---------------------------------------------------------------------------
// Derived atoms
// ---------------------------------------------------------------------------

export const isTridionDocAtom = atom(
  get => get(documentAtom).document?.sourceSystem === 'TRIDION',
);

export const translatedDocumentNameAtom = atom(get => {
  const { document, isTranslating } = get(documentAtom);
  return isTranslating ? document?.otherName : document?.name;
});

// ---------------------------------------------------------------------------
// Action atoms — UI state
// ---------------------------------------------------------------------------

export const toggleTranslationAtom = atom(null, (get, set) => {
  const prev = get(documentAtom);
  set(documentAtom, {
    ...prev,
    isTranslating: !prev.isTranslating,
  });
});

export const setChosenDocumentCountryAtom = atom(
  null,
  (get, set, value: ContentReference['country'] | null) => {
    set(documentAtom, {
      ...get(documentAtom),
      chosenDocumentCountry: value,
    });
  },
);

// ---------------------------------------------------------------------------
// URL-encoded game fullscreen
// ---------------------------------------------------------------------------

export const useGameFullscreen = () =>
  useQueryState('gameFullscreen', parseAsBoolean.withDefault(false));
